import type { RangeOption } from "../../../constants/epicLeaderboard";
import type { PkFeedRow } from "../../../types/playerPk";
import { formatEtDateTime } from "../../../utils/etDate";
import PlayerLink from "../../common/PlayerLink";
import "../../epicLeaderboard/epicLeaderboard.css";
import "./pk.css";

const ROLE_LABELS = { kill: "Kill", death: "Death", assist: "Assist" } as const;

const TypeBadge = ({ row }: { row: PkFeedRow }) => {
  if (row.type === "hc") {
    return (
      <span className="pk-badge pk-badge--type pk-badge--hc">
        HC{row.hcLevel ? ` ${row.hcLevel}` : ""}
      </span>
    );
  }
  if (row.type === "wanted") {
    return (
      <span className="pk-badge pk-badge--type pk-badge--wanted">Wanted</span>
    );
  }
  return <span className="pk-badge pk-badge--type">PK</span>;
};

const clanLabel = (clan: string | null | undefined) =>
  clan ? <span className="epic-lb-muted"> [{clan}]</span> : null;

type Props = {
  row: PkFeedRow;
  range: RangeOption;
  // The player whose page this is; left out of the "assists:" list since the
  // row is already from their point of view.
  viewer: string;
};

// One PK event from the viewed player's perspective.
const PkFeedRowItem = ({ row, range, viewer }: Props) => {
  const others = row.assists.filter(
    (a) => a.player.toLowerCase() !== viewer.toLowerCase()
  );
  const link = (name: string) => (
    <PlayerLink className="pk-link" player={name} range={range} tab="pk" />
  );

  let main;
  if (row.role === "kill") {
    main = (
      <>
        Killed {link(row.killed)}
        {clanLabel(row.killedClan)}
      </>
    );
  } else if (row.role === "death") {
    main = (
      <>
        Killed by {link(row.killer)}
        {clanLabel(row.killerClan)}
      </>
    );
  } else {
    main = (
      <>
        Assisted {link(row.killer)}
        {clanLabel(row.killerClan)} against {link(row.killed)}
        {clanLabel(row.killedClan)}
      </>
    );
  }

  return (
    <div className="pk-feed-row">
      <div className="pk-feed-badges">
        <span className={`pk-badge pk-badge--${row.role}`}>
          {ROLE_LABELS[row.role]}
        </span>
        <TypeBadge row={row} />
      </div>
      <div className="pk-feed-main">
        {main}
        {others.length > 0 && (
          <div className="epic-lb-muted">
            assists:{" "}
            {others.map((a, i) => (
              <span key={`${a.player}-${i}`}>
                {i > 0 && ", "}
                {a.player}
                {a.clan ? ` [${a.clan}]` : ""}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="pk-feed-date">{formatEtDateTime(row.dateEt)}</div>
    </div>
  );
};

export default PkFeedRowItem;
